import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertTriangle, MessageCircleQuestion } from "lucide-react";
import { HTMLAttributes, ReactNode } from "react";
import { Button } from "./button";
import Dialog from "./dialog";
import Feedback from "./feedback";

const alertVariants = cva(
  "relative w-full max-w-2xl rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-brand-error/50 text-brand-error [&>svg]:text-brand-error"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

const Alert = ({
  className,
  title,
  description,
  variant,
  disableReport = false,
  ...props
}: Omit<HTMLAttributes<HTMLDivElement>, "children"> &
  VariantProps<typeof alertVariants> & {
    title: string;
    description: ReactNode;
    disableReport?: boolean;
  }) => (
  <div
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  >
    <AlertTriangle size={16} />
    <h5
      className={cn(
        "mb-2.5 mt-1.5 font-medium leading-none tracking-tight",
        className
      )}
    >
      {title}
    </h5>
    <div
      data-testid="alertDescription"
      className={cn("text-sm [&_p]:leading-relaxed font-light", className)}
    >
      {description}
    </div>
    {!disableReport && (
      <Feedback>
        {() => (
          <Dialog.Trigger asChild>
            <Button variant="ghost" className="block ml-auto mr-0 mt-4">
              <MessageCircleQuestion
                strokeWidth={1.6}
                className="h-[20px] w-[20px] relative -left-2 -top-0.5 inline"
              />
              Report Issue
            </Button>
          </Dialog.Trigger>
        )}
      </Feedback>
    )}
  </div>
);

export default Alert;
